import isEqual from 'lodash/isEqual';
import type { StringKeyOf } from 'type-fest';

import type { FieldValue, Filter } from '@models';

/**
 * Values that should be ignored when checking for filter presence. This does
 * not include `false` or `0` since these are valid values to filter on.
 */
const falsyFieldValues = [undefined, null, ''] as FieldValue[];

/**
 * Returns true if the given filter value represents an active filter.
 */
export const isTruthyFilterValue = (value: FieldValue) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return !falsyFieldValues.includes(
    typeof value === 'string' ? value.trim() : value,
  );
};

/**
 * Returns true if any value in the given filters object is truthy.
 */
export const hasActiveFilters = (
  filters: Record<string, FieldValue> | object,
) => Object.values(filters).some(value => isTruthyFilterValue(value));

/**
 * Adds a filter to the given filter state using the filter's default data as
 * the initial value. Encapsulates a cast that would otherwise be required at
 * every call site: `Filter.data` is typed as the wide `FieldValue` union and
 * can't be proven to match `State[filter.key]` without discriminating on the
 * key.
 */
export const withFilter = <State>(
  state: Partial<State>,
  filter: Filter<StringKeyOf<State>>,
): Partial<State> =>
  ({
    ...state,
    [filter.key]: filter.data,
  }) as Partial<State>;

/**
 * Returns the active filter keys from a filter state. Encapsulates the
 * well-known `Object.keys` typing limitation (returns `string[]` regardless of
 * the input type).
 */
export const activeFilterKeys = <State>(
  state: Partial<State>,
): StringKeyOf<State>[] => Object.keys(state) as StringKeyOf<State>[];

/**
 * Indexes filter definitions by key, so a state or query key can be resolved to
 * its definition without the definition list's order leaking into the result.
 */
export const filterDefinitions = <K extends string>(
  filters: readonly Filter<K>[],
): Map<string, Filter<K>> =>
  new Map(filters.map(filter => [filter.key, filter]));

/**
 * Returns true if the FilterBar has visible content (at least one selected
 * filter pill or a Reset button) for the given state.
 */
export const shouldRenderFilterBar = <State>(
  values: Partial<State>,
  filters: Filter<StringKeyOf<State>>[],
  defaults: Partial<State>,
): boolean =>
  filters.some(filter => filter.key in values) || !isEqual(values, defaults);
